import React, { useEffect, useState } from "react";
import "../styles/globals.css";
import type { AppProps } from "next/app";
import AOS from "aos";
import "aos/dist/aos.css";
import { ContextProvider } from "../context";
import config from "../config";
import { getCookie } from "cookies-next";
import Seo from "../components/Micro/Seo";
import NextNProgress from "nextjs-progressbar";
import { useRouter } from "next/router";
import useUser from "../hooks/useUser";
import DashboardLayout from "../layouts/DashboardLayout";
import Head from "next/head";
import TeamLayout from "../layouts/TeamLayout";
import SwrProvider from "../helpers/Swr";
import Script from "next/script";
import useTheme from "../hooks/useTheme";
import { Toaster } from "sonner";
import { ShortcutKeys } from "../hooks/useShortcut";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import DesktopOnlyNotice from "@/components/MobileView";
import Environment from "@/enum/environment.enum";
import { isMobile } from "react-device-detect";
import StoreInitializer from "@/context/store-initializer";
const isMobileDevice = () => {
  if (typeof window === "undefined") return false;
  const userAgent =
    navigator.userAgent || navigator.vendor || (window as any).opera;
  return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
    userAgent.toLowerCase(),
  );
};

const exempted_paths =
  /^\/integrations\/manage\/.*|^\/login|^\/register|^\/onboard|^\/teams\/\[team\]\/invitation|^\/teams\/\[team\]\/integrations\/manage|^\/teams\/\[team\]\/members\/invite|^\/teams\/new|^\/domains\/new|^\/success|^\/logout|^\/profile\/upgrade/;

if (
  typeof window !== "undefined" &&
  !window.location.host.includes("127.0.0.1") &&
  !window.location.host.includes("localhost")
) {
  posthog.init(config.POSTHOG_KEY, {
    api_host: config.POSTHOG_HOST || "https://app.posthog.com",
    mask_all_text: false,
    mask_personal_data_properties: false,
    mask_all_element_attributes: false,
    session_recording: {
      maskAllInputs: false,
      maskInputOptions: {
        password: true,
      },
    },
    loaded: (posthog) => {
      if (config.ENVIRONMENT === Environment.DEVELOPMENT) posthog.debug();
    },
  });
}

function App({ Component, pageProps }: AppProps) {
  const router = useRouter();
  const { theme } = useTheme();
  const isAuthRoute =
    router.pathname.startsWith("/login") ||
    router.pathname.startsWith("/register");

  const { user } = useUser({ haltRequest: isAuthRoute });

  const [isMobileClient, setIsMobileClient] = useState(false);

  useEffect(() => {
    const mobileDetected = isMobile || isMobileDevice();
    setIsMobileClient(mobileDetected);

    const handleResize = () => {
      const mobile = isMobile || isMobileDevice();
      setIsMobileClient(mobile);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    AOS.init();
    AOS.refresh();
  }, []);

  useEffect(() => {
    if (isAuthRoute) return;
    if (user) {
      const win = window as any;

      if (typeof win.Featurebase !== "function") {
        win.Featurebase = function (...args: any[]) {
          (win.Featurebase.q = win.Featurebase.q || []).push(args);
        };
      }

      win.Featurebase("identify", {
        organization: "brimble",
        email: user.email,
        name: user.username || user.first_name,
        id: user.id,
        profilePicture:
          user.avatar ||
          `${config.AVATAR_URL}/adventurer-neutral/svg?seed=${
            user.username || user.first_name
          }`,
      });

      win.Featurebase("initialize_feedback_widget", {
        organization: "brimble",
        theme: getCookie("theme") || "light",
        email: user.email,
      });

      win.Featurebase("init_changelog_widget", {
        organization: "brimble",
        theme: getCookie("theme") || "light",
        popup: {
          enabled: true,
          usersName: user.username || user.first_name,
          autoOpenForNewUpdates: true,
        },
      });

      win.Featurebase("initialize_survey_widget", {
        organization: "brimble",
        theme: getCookie("theme") || "light",
        placement: "bottom-left",
        email: user.email,
      });
    }
  }, [isAuthRoute, user]);

  if (isMobileClient && !exempted_paths.test(router.pathname)) {
    return (
      <>
        <Head>
          <meta
            name="viewport"
            content="minimum-scale=1, initial-scale=1, width=device-width, shrink-to-fit=no, user-scalable=no, viewport-fit=cover"
          />
        </Head>
        <Seo
          title="Desktop Only - Brimble Dashboard"
          description="The Brimble dashboard is only available on desktop devices."
        />
        <DesktopOnlyNotice />
      </>
    );
  }

  return (
    <>
      <PostHogProvider client={posthog}>
        <Head>
          <meta
            name="viewport"
            content="minimum-scale=1, initial-scale=1, width=device-width, shrink-to-fit=no, user-scalable=no, viewport-fit=cover"
          />
        </Head>
        <Script id="cusomterchat-script" strategy="afterInteractive">
          {`
            (function(d,t) {
              var BASE_URL="https://app.chatwoot.com";
              var g=d.createElement(t),s=d.getElementsByTagName(t)[0];
              g.src=BASE_URL+"/packs/js/sdk.js";
              g.defer = true;
              g.async = true;
              s.parentNode.insertBefore(g,s);
              g.onload=function(){
                window.chatwootSDK.run({
                  websiteToken: 'mn5KENDDuZxcSc6bxFE5S3A9',
                  baseUrl: BASE_URL
                })
              }
            })(document,"script");
          `}
        </Script>
        <Script
          src="https://do.featurebase.app/js/sdk.js"
          id="featurebase-sdk"
        />

        <ContextProvider>
          <SwrProvider>
            <StoreInitializer />
            <Seo title="Dashboard" description="Deploy Fast Websites, Faster" />
            <NextNProgress color="#3366CC" height={3} />
            {exempted_paths.test(router.pathname) ? (
              <Component {...pageProps} />
            ) : (
              <>
                {router.pathname.startsWith("/teams/[team]") ? (
                  <TeamLayout>
                    <Component {...pageProps} />
                  </TeamLayout>
                ) : (
                  <DashboardLayout>
                    <Component {...pageProps} />
                  </DashboardLayout>
                )}
              </>
            )}
            <Toaster
              duration={5000}
              richColors
              theme={theme}
              position="bottom-center"
              style={{
                zIndex: 99999999999999,
              }}
              closeButton
            />
            {!isAuthRoute && <ShortcutKeys />}
          </SwrProvider>
        </ContextProvider>
      </PostHogProvider>
    </>
  );
}

export default App;
if (App) App.getInitialProps = async () => ({});
